import { swLog } from 'serviceWorker/helpers/makeSwLogger';
import { LogLevel } from 'shared/types/logger';
import { add as editIssueStrategy } from 'serviceWorker/db/httpRequests';
import {
  HttpRequestModelType,
  HttpRequestStatus,
  __HttpRequestModel__,
} from 'shared/domain/httpRequest/httpRequestModel';
import {
  UpdateIssueOutDto,
} from 'shared/dtos/out/issue';
import { LabelledEntity } from 'shared/types/commonView';
import { debugLog } from 'shared/logger/debugLog';
import {
  EditIssueModel,
  IssueModel,
} from '../../../../shared/domain/issue/issueModel';
import { EditIssueUseCase } from '../../../../shared/domain/issue/types';
import type { IssueRequestData } from './issueRequestData';

function makeEditIssueUseCase(
  editIssueStrategy: (editIssueModel: __HttpRequestModel__) => Promise<any>
): EditIssueUseCase {
  return function editIssue(issueAddress, editIssueModel, uniqueId): Promise<void> {
    debugLog('editIssue', issueAddress, editIssueModel);
    const data: IssueRequestData<UpdateIssueOutDto> = {
      body: editIssueModeltoChangesOutDto(editIssueModel),
      uniqueId,
      issueId: issueAddress.issueId,
    };

    return editIssueStrategy({
      createdAt: Date.now(),
      method: 'PUT',
      url: `project/${issueAddress.projectId}/issue/${issueAddress.issueId}`,
      data,
      entityType: HttpRequestModelType.issue,
      status: HttpRequestStatus.NEW,
    });
  };
}

const editIssue = makeEditIssueUseCase(editIssueStrategy);
export default editIssue;

function makeEditIssueModelExtendedDataOutDtoMap(): {
  [key in keyof IssueModel['extendedData']]: (p: any) => any;
} {
  return {
    workTypes: labelledEntitiesToStrings,
    rootCauses: labelledEntitiesToStrings,
    estimatedCost: costs,
    finalCost: costs,
    targetCompletionDate: identity,
    finalCompletionDate: identity,
    targetResponseToAuthorDate: identity,
    finalResponseToAuthorDate: identity,
    solutionProposal: identity,
    impact: labelledEntityToString,
    environmentalAspect: labelledEntityToString,
    effect: labelledEntityToString,
    hazardCategory: labelledEntitiesToStrings,
    expectedFine: identity,
    imposedFine: identity,
    solutionMethod: identity,
    subcontractorRepresentative: identity,
    decisionToImposeFine: labelledEntityToString,
    circumstances: labelledEntityToString,
    issueVerification: labelledEntityToString,
    delayReason: labelledEntityToString,
    rejectionReason: labelledEntityToString,
    proposedCorrectiveAction: labelledEntityToString,
    costCode: identity,
    daysOfInabilityToWork: identity,
    personUnableToWork: identity,
    spilledSubstance: labelledEntityToString,
    waterLeakageScale: identity,
    soilLeakageScale: identity,
    contaminatedSoilScale: identity,
    targetStartDate: identity,
    finalStartDate: identity,
    executedByCompanies: labelledEntitiesToStrings,
    targetAmount: identity,
    completedAmount: identity,
    numberOfEmployees: identity,
    priority: labelledEntityToString,
    projectStage: labelledEntityToString,
    sickLeaveStartDate: identity,
    sickLeaveEndDate: identity,
    supervisor: labelledEntityToString,
    victim: identity,
    victimJobTitle: identity,
    employeeNumber: identity,
    employmentType: labelledEntityToString,
    immediateMeasures: identity,
    victimActivity: identity,
    injuryTypes: labelledEntitiesToStrings,
    injuredBodyParts: labelledEntitiesToStrings,
    applicationNumber: identity,
    productType: identity,
    producerDistributor: identity,
    typeModel: identity,
    installationLocation: identity,
    requirements: labelledEntitiesToStrings,
    installationBasis: labelledEntitiesToStrings,
    constructionProductDocuments: labelledEntitiesToStrings,
    deviceDocuments: labelledEntitiesToStrings,
    additionalDocuments: labelledEntitiesToStrings,
  };
}

function makeEditIssueModelPrimaryDataOutDtoMap(): {
  [key in keyof IssueModel['primaryData']]: (p: any) => any;
} {
  return {
    site: labelledEntityToString,
    level: labelledEntityToString,
    title: identity,
    assignee: labelledEntityToString,
    description: identity,
    detectionDate: identity,
    subcontractors: labelledEntitiesToStrings,
    contractNumbers: labelledEntitiesToStrings,
    executor: labelledEntityToString,
    location: identity,
  };
}

/** A change the API has no field for is left out (and logged) instead of failing the whole edit. */
function logUnmappedEditKey(section: string, key: string): void {
  swLog(`Issue edit has no API field for ${section}.${key}`, LogLevel.ERROR, null, { entityName: 'issues', query: { section, key } });
}

function editIssueModeltoChangesOutDto(
  changesModel: Omit<EditIssueModel, '_id'>
): UpdateIssueOutDto {
  const outDto: UpdateIssueOutDto = {};
  if (changesModel.extendedData) {
    const mappings = makeEditIssueModelExtendedDataOutDtoMap();
    outDto.extendedData = {} as UpdateIssueOutDto['extendedData'];
    const keys = Object.keys(changesModel.extendedData);
    // @ts-ignore Object.keys defined badly
    keys.forEach((key: keyof EditIssueModel['extendedData']) => {
      if (!mappings[key]) {
        logUnmappedEditKey('extendedData', key);
        return;
      }
      // @ts-ignore Object.keys defined badly
      outDto.extendedData[key] = mappings[key](
        // @ts-ignore Object.keys defined badly
        changesModel.extendedData[key]
      );
    });
  }
  if (changesModel.primaryData) {
    const mappings = makeEditIssueModelPrimaryDataOutDtoMap();
    outDto.primaryData = {};
    const keys = Object.keys(changesModel.primaryData);
    // @ts-ignore Object.keys defined badly
    keys.forEach((key: keyof EditIssueModel['primaryData']) => {
      if (!mappings[key]) {
        logUnmappedEditKey('primaryData', key);
        return;
      }
      // @ts-ignore Object.keys defined badly
      outDto.primaryData[key] = mappings[key](
        // @ts-ignore Object.keys defined badly
        changesModel.primaryData[key]
      );
    });
  }

  if (changesModel.stageId) {
    outDto.stageId = changesModel.stageId;
  }
  if (changesModel.mainImage || changesModel.mainImage === null) {
    outDto.mainImage = changesModel.mainImage;
  }

  return outDto;
}

function labelledEntitiesToStrings(
  labelledEntities: LabelledEntity[]
): string[] {
  return labelledEntities.map((le) => le._id);
}

function labelledEntityToString(
  labelledEntity: LabelledEntity | undefined | null
): string | undefined | null {
  if (labelledEntity === null) return labelledEntity;
  return labelledEntity?._id;
}

function costs(costs: any[]): any {
  return costs.map((cost) => {
    return {
      ...cost,
      coveredBy: cost.coveredBy?._id,
    };
  });
}

function identity<T>(x: T): T {
  return x;
}
